/**
 * Table order persistence helper.
 * Handles IndexedDB persistence for table order items and separates it from Vuex mutations.
 */

import indexDbHelper from "./indexDbHelper";
import { INDEXED_DB_STORES } from "./tableOrderConstants";

const STORE_NAME = INDEXED_DB_STORES.TABLE_ORDER_ITEMS;

function getNewItems(items) {
  return (items || []).filter((item) => item.isSent !== true);
}

/**
 * Prepare items for IndexedDB storage
 * Ensures each item has a local_id
 * @param {Array} items - Items to prepare
 * @returns {Array} Items with valid local_id
 */
function prepareItemsForDb(items) {
  return items
    .map((item) => ({
      ...item,
      local_id: item.local_id || item.cartId || `${item.id}-${Date.now()}`
    }))
    .filter((item) => item.local_id);
}

/**
 * Persist new items to IndexedDB
 * Only persists items that are not sent (isSent !== true)
 * @param {Array} cartItems - All cart items
 * @returns {Promise<void>}
 */
export async function persistCartItemsToDb(cartItems) {
  try {
    const newItems = getNewItems(cartItems);

    if (newItems.length === 0) {
      await indexDbHelper.clearAllData(STORE_NAME);
      return;
    }

    const itemsForDb = prepareItemsForDb(newItems);
    if (itemsForDb.length > 0) {
      await indexDbHelper.saveToDb(STORE_NAME, itemsForDb);
    }
  } catch (error) {
    console.error(
      "[tableOrderPersistence] Error persisting cart items:",
      error
    );
    // Don't throw - persistence errors shouldn't break the app
  }
}

/**
 * Delete a single item from IndexedDB
 * @param {string} localId - Local ID of the item to delete
 * @returns {Promise<void>}
 */
export async function deleteItemFromDb(localId) {
  try {
    if (localId) {
      await indexDbHelper.deleteSingleInstance(STORE_NAME, localId);
    }
  } catch (error) {
    console.error("[tableOrderPersistence] Error deleting item:", error);
    // Don't throw - persistence errors shouldn't break the app
  }
}

/**
 * Clear all items from IndexedDB
 * @returns {Promise<void>}
 */
export async function clearAllItemsFromDb() {
  try {
    await indexDbHelper.clearAllData(STORE_NAME);
  } catch (error) {
    console.error("[tableOrderPersistence] Error clearing items:", error);
    // Don't throw - persistence errors shouldn't break the app
  }
}

/**
 * Persist a single item addition
 * Used when adding a new item to cart
 * @param {Array} cartItems - All cart items after addition
 * @returns {Promise<void>}
 */
export async function persistItemAddition(cartItems) {
  await persistCartItemsToDb(cartItems);
}

/**
 * Persist item quantity update
 * Used when updating quantity of an existing item
 * @param {Array} cartItems - All cart items after update
 * @returns {Promise<void>}
 */
export async function persistItemUpdate(cartItems) {
  await persistCartItemsToDb(cartItems);
}
